import { Link, Typography } from '@mui/material';
import pino from 'pino';
import { useState } from 'react';
import type {
	ChangeEvent,
	CSSProperties,
	FC,
	FocusEvent as ReactFocusEvent,
	FormEvent,
	PropsWithChildren,
} from 'react';
import { z } from 'zod';

import { notificationStrings, sessionStrings } from '../../../../../../../constants/strings';
import { useSendMfaCodeQuery } from '../../../../../../../queries/sessionQuery';
import { useZustand } from '../../../../../../../zustandStore';
import { invalidOtpCodeSentMessage } from '../../../../activateForm/setupTwoFactorAuth';
import FormWrapper from '../../../formWrapper';
import { useValidation } from '../../../hooks/useValidation';
import FormInput from '../../common/formInput';

const logger = pino();

export const twoFactorSchema = z.object({
	authCode: z.string().min(6, 'Authentication code must be at least 6 characters long'),
});

export type TwoFactorSchemaType = z.infer<typeof twoFactorSchema>;

type SetupTwoFactorAuthProps = {
	style?: CSSProperties;
};

const SetupTwoFactorAuth: FC<PropsWithChildren<SetupTwoFactorAuthProps>> = ({ style = {} }) => {
	const [showSecretKey, setShowSecretKey] = useState(false);
	const { setPageLoading, steps, setNotification, setStepsData } = useZustand(state => ({
		...state,
	}));
	const [formState, setFormState] = useState<TwoFactorSchemaType>({
		authCode: '',
	});
	const { validateField, clearFieldError, fieldErrors, validateAllSchema } = useValidation<TwoFactorSchemaType>();
	const sendMfaCode = useSendMfaCodeQuery();

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormState(prev => {
			const updatedState = { ...prev, [name]: value };
			clearFieldError(name as keyof TwoFactorSchemaType);
			return updatedState;
		});
	};

	const handleBlur = (e?: ReactFocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (e) {
			const { name = '' } = e.target;
			validateField(twoFactorSchema, name as keyof TwoFactorSchemaType, formState);
		}
	};

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		validateAllSchema(twoFactorSchema, formState);
		sendMfaCode.mutate(
			{ code: formState.authCode, challengeName: 'SOFTWARE_TOKEN_MFA' },
			{
				onSuccess: async data => {
					setStepsData({
						mfaOtp: {
							status: data.mfaOtp.status,
							secret: data.mfaOtp.secret || '',
							qrCode: data.mfaOtp.qrCode,
						},
						phoneSetup: {
							status: data.phoneSetup.status,
							phoneDetails: data.phoneSetup.phoneDetails,
						},
					});
					if (data.mfaOtp.status === 'authenticated') {
						setFormState({ authCode: '' });
					}
				},
				onError: error => {
					logger.error({ error });
					setNotification([
						{
							type: notificationStrings.ERROR,
							message: invalidOtpCodeSentMessage,
						},
					]);
				},
				onSettled: () => {
					setPageLoading(false);
				},
			},
		);
	};

	return (
		<FormWrapper
			style={style}
			formName="Setup your Two Factor Authentication"
			onSubmit={handleSubmit}
			submitButton={{
				label: sessionStrings.CONTINUE,
				isDisabled: !twoFactorSchema.safeParse(formState).success,
			}}
			isFormPending={sendMfaCode.isPending}
		>
			<Typography variant="body1" gutterBottom>
				2FA, or two factor authentication, helps reduce the risk of unauthorized access to your account. Enable 2FA and
				gain peace of mind that your account is secure.
			</Typography>
			<Typography variant="body1" gutterBottom>
				To start, install an authenticator application of your choice on your phone. Popular authenticator apps include
				Google Authenticator, Authy or Microsoft Authenticator.
			</Typography>
			<Typography variant="body1" gutterBottom>
				Once installed follow the instructions to add or setup an account by scanning the QR code.
			</Typography>
			<div style={{ textAlign: 'center', margin: '20px 0' }}>
				<img src={steps.data.mfaOtp?.qrCode || ''} alt="QR Code" />
			</div>
			<Link
				href="#"
				onClick={e => {
					e.preventDefault();
					setShowSecretKey(!showSecretKey);
				}}
				style={{
					fontWeight: '600',
					fontSize: '12px',
					lineHeight: '20px',
					alignItems: 'center',
					textAlign: 'center',
					textTransform: 'uppercase',
					color: 'rgb(0, 174, 255)',
					display: 'block',
					margin: 'auto',
					width: '100%',
					textDecoration: 'none',
				}}
			>
				{showSecretKey ? 'HIDE SECRET KEY' : 'SHOW SECRET KEY'}
			</Link>
			{showSecretKey && (
				<Typography variant="body1" gutterBottom style={{ wordBreak: 'break-word' }}>
					Secret Key: {steps.data.mfaOtp?.secret || 'Not Available'}
				</Typography>
			)}
			<FormInput
				label="Enter a code from your authenticator app"
				name="authCode"
				value={formState.authCode}
				error={fieldErrors['authCode']}
				onChange={handleInputChange}
				onBlur={handleBlur}
			/>
		</FormWrapper>
	);
};

export default SetupTwoFactorAuth;
